// src/components/PrivacyPolicy.js

import React from 'react';
import { Helmet } from 'react-helmet';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-100 p-8 text-black">
      <Helmet>
        <title>Privacy Policy - Propertise AI Group</title>
      </Helmet>
      <div className="max-w-4xl mx-auto bg-white p-8 rounded shadow">
        <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
        
        {/* Effective Date and Version Control */}
        <div className="mb-6">
          <p className="mb-2">
            <strong>Effective Date:</strong> November 21, 2024
          </p>
          <p className="mb-2">
            <strong>Version:</strong> 1.0
          </p>
          <p>
            This Privacy Policy has been last updated on the above date and version. Please review it periodically for any changes.
          </p>
        </div>

        <p className="mb-4">
          Your privacy is important to us. Propertise AI Group ("we," "us," or "our") is committed to protecting and respecting your privacy regarding any information we may collect from you through our mobile application ("App") and related services.
        </p>

        <h2 className="text-2xl font-semibold mt-6 mb-4">Information We Collect</h2>
        
        <p className="mb-4">
          We may collect and process the following types of personal information:
        </p>
        
        <ul className="list-disc list-inside mb-4">
          <li><strong>Personal Identification Information:</strong> Name, email address, phone number, and other contact details you provide during registration or use of our App.</li>
          <li><strong>Property Information:</strong> Details about properties you scan, including address, size, features, and other relevant data.</li>
          <li><strong>Photographic Data:</strong> Photos of properties taken using the App to assist in generating valuation reports.</li>
          <li><strong>Usage Data:</strong> Information on how you interact with the App, such as access times, pages viewed, and actions taken.</li>
          <li><strong>Device Information:</strong> Data about the device you use to access the App, including device type, operating system, and unique device identifiers.</li>
          <li><strong>Location Data:</strong> Geolocation information to provide location-based services and improve user experience.</li>
        </ul>

        <h2 className="text-2xl font-semibold mt-6 mb-4">How We Use Your Information</h2>
        
        <p className="mb-4">
          We use the collected information for the following purposes:
        </p>
        
        <ul className="list-disc list-inside mb-4">
          <li><strong>Service Provision:</strong> To operate and maintain our App, including generating accurate PDF valuation reports based on the data and photos you provide.</li>
          <li><strong>Improvement of Services:</strong> To analyze usage patterns and feedback to enhance and develop new features for the App.</li>
          <li><strong>Communication:</strong> To respond to your inquiries, send updates, and provide customer support.</li>
          <li><strong>Personalization:</strong> To tailor the App experience based on your preferences and usage history.</li>
          <li><strong>Security:</strong> To protect against unauthorized access, fraud, and other security threats.</li>
          <li><strong>Compliance:</strong> To comply with legal obligations and enforce our Terms of Service.</li>
        </ul>

        <h2 className="text-2xl font-semibold mt-6 mb-4">Data Sharing and Disclosure</h2>
        
        <p className="mb-4">
          We may share your information in the following circumstances:
        </p>
        
        <ul className="list-disc list-inside mb-4">
          <li><strong>Service Providers:</strong> Third-party vendors who perform services on our behalf, such as data storage, payment processing, and analytics.</li>
          <li><strong>Legal Requirements:</strong> When required by law, regulation, or legal process, or to protect our rights, property, or safety.</li>
          <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of all or part of our assets, your information may be transferred as part of that transaction.</li>
          <li><strong>With Your Consent:</strong> We may share your information with other parties if you have given us explicit consent to do so.</li>
        </ul>

        <h2 className="text-2xl font-semibold mt-6 mb-4">Data Storage and Security</h2>
        
        <p className="mb-4">
          We implement a variety of security measures to maintain the safety of your personal information. These measures include encryption, secure servers, and regular security assessments to protect against unauthorized access, alteration, disclosure, or destruction of your data.
        </p>
        
        <p className="mb-4">
          However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
        </p>

        <h2 className="text-2xl font-semibold mt-6 mb-4">Data Retention</h2>
        
        <p className="mb-4">
          We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy unless a longer retention period is required or permitted by law.
        </p>

        <h2 className="text-2xl font-semibold mt-6 mb-4">Your Rights</h2>
        
        <p className="mb-4">
          Depending on your jurisdiction, you may have the following rights regarding your personal information:
        </p>
        
        <ul className="list-disc list-inside mb-4">
          <li><strong>Access:</strong> Request access to your personal data.</li>
          <li><strong>Correction:</strong> Request correction of any inaccurate or incomplete data.</li>
          <li><strong>Deletion:</strong> Request deletion of your personal information under certain conditions.</li>
          <li><strong>Restriction:</strong> Request restriction of processing your data.</li>
          <li><strong>Data Portability:</strong> Request transfer of your data to another service.</li>
          <li><strong>Objection:</strong> Object to the processing of your data for specific purposes.</li>
        </ul>
        
        <p className="mb-4">
          To exercise these rights, please contact us using the details provided in the "Contact Us" section below.
        </p>

        <h2 className="text-2xl font-semibold mt-6 mb-4">Children's Privacy</h2>
        
        <p className="mb-4">
          Our App is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that a child has provided us with personal information, we will take steps to delete such information promptly.
        </p>

        <h2 className="text-2xl font-semibold mt-6 mb-4">International Data Transfers</h2>
        
        <p className="mb-4">
          Your information may be transferred to and maintained on servers located outside your country of residence. By using our App, you consent to the transfer of your information to countries that may have different data protection laws than your own.
        </p>

        <h2 className="text-2xl font-semibold mt-6 mb-4">Changes to This Privacy Policy</h2>
        
        <p className="mb-4">
          We may update our Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date and version number. We encourage you to review this Privacy Policy periodically to stay informed about how we are protecting your information.
        </p>

        {/* Version History Section */}
        <h2 className="text-2xl font-semibold mt-6 mb-4">Version History</h2>
        
        <table className="min-w-full table-auto mb-6">
          <thead>
            <tr>
              <th className="px-4 py-2 border">Version</th>
              <th className="px-4 py-2 border">Effective Date</th>
              <th className="px-4 py-2 border">Description of Changes</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-4 py-2 border text-center">1.0</td>
              <td className="px-4 py-2 border text-center">November 21, 2024</td>
              <td className="px-4 py-2 border">Initial version of the Privacy Policy, including detailed information on data collection, usage, sharing, and user rights.</td>
            </tr>
            {/* Future versions can be added here */}
          </tbody>
        </table>

        <h2 className="text-2xl font-semibold mt-6 mb-4">Contact Us</h2>
        
        <p className="mb-4">
          If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:
        </p>
        
        <p className="mb-4">
          <strong>Email:</strong> <a href="mailto:info@propertiseaigroup.com.au" className="text-blue-600 underline">info@propertiseaigroup.com.au</a><br />
          <strong>Address:</strong> 402/2 Goatcher Vista, Shenton Park, WA 6008
        </p>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
